
import { api } from './base';
import { cachedFetch, invalidateCached } from '@/shared/lib/refCache';

export interface KpiLeaderboardRow {
  rank: number;
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  points: number;
  ticketsClosed: number;
  avgRating: number | null;
  ratedCount: number;
}

export interface KpiLeaderboard {
  month: string;
  rows: KpiLeaderboardRow[];
}

export interface KpiComplexityLevel {
  class: number;
  multiplier: number;
  label: string;
}

export interface KpiSettings {
  defaultBasePoints: number;
  defaultTargetMinutes: number | null;
}

export interface KpiSatisfactionLevel {
  rating: number;
  multiplier: number;
  label: string;
}

export interface KpiUserSummary {
  monthPoints: number;
  monthTicketsClosed: number;
  totalPoints: number;
  totalTicketsClosed: number;
  avgRating: number | null;
  ratedCount: number;
}

export interface UserProfile {
  user: {
    id: string;
    displayName: string;
    username: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    middleName?: string | null;
    department?: string | null;
    phone?: string | null;
    orgPath?: string[];
    avatarUrl: string | null;
    createdAt: string | null;
    keycloakSyncedAt: string | null;
  };
  roles: string[];
  groups: { id: string; key: string; name: string; publicName: string | null }[];
  ticketStats: {
    createdTotal: number;
    createdOpen: number;
    assignedTotal: number;
    assignedSolved: number;
  };
  kpi: KpiUserSummary;
  clientInfo: Record<string, unknown> | null;
  lastLoginAt: string | null;
}

export interface KpiUserEvent {
  ticketId: string;
  ticketTitle: string | null;
  points: number;
  basePoints: number | null;
  speedMultiplier: number | null;
  complexityMultiplier: number | null;
  shareDivisor: number | null;
  reason: 'ticket_closed' | 'reversal' | string;
  createdAt: string;
}

export interface KpiUserEvents {
  month: string;
  events: KpiUserEvent[];
}

export const kpiApi = {
  leaderboard: (month?: string) =>
    api.get<KpiLeaderboard>('/api/v1/kpi/leaderboard', month ? { month } : undefined),

  months: () => api.get<string[]>('/api/v1/kpi/months'),

  userEvents: (userId: string, month?: string) =>
    api.get<KpiUserEvents>(`/api/v1/kpi/users/${userId}/events`, month ? { month } : undefined),

  settings: () =>
    cachedFetch('kpi:settings', 5 * 60_000, () => api.get<KpiSettings>('/api/v1/kpi/settings')),

  updateSettings: (dto: KpiSettings) =>
    api.put<KpiSettings>('/api/v1/kpi/settings', dto).then((res) => {
      invalidateCached('kpi:settings');
      return res;
    }),

  complexityLevels: () =>
    cachedFetch('kpi:complexity-levels', 5 * 60_000, () =>
      api.get<KpiComplexityLevel[]>('/api/v1/kpi/complexity-levels'),
    ),

  updateComplexityLevels: (levels: KpiComplexityLevel[]) =>
    api.put<KpiComplexityLevel[]>('/api/v1/kpi/complexity-levels', { levels }).then((res) => {
      invalidateCached('kpi:complexity-levels');
      return res;
    }),

  satisfactionLevels: () =>
    cachedFetch('kpi:satisfaction-levels', 5 * 60_000, () =>
      api.get<KpiSatisfactionLevel[]>('/api/v1/kpi/satisfaction-levels'),
    ),

  updateSatisfactionLevels: (levels: KpiSatisfactionLevel[]) =>
    api.put<KpiSatisfactionLevel[]>('/api/v1/kpi/satisfaction-levels', { levels }).then((res) => {
      invalidateCached('kpi:satisfaction-levels');
      return res;
    }),
};

export const userProfileApi = {
  get: (userId: string) => api.get<UserProfile>(`/api/v1/users/${userId}/profile`),
};
